import { useEffect, useRef, useState } from "react";
import { fetchMessages } from "../../../chatApi";
import { onAnyNewMessage } from "../../../chatSocket";
import type { ChatMessage } from "../../../types";

// Single shared source for Together Mode's chat, split three ways:
// TogetherChatStream.tsx (fading bubbles, always visible), TogetherChatInput
// .tsx (the always-visible composer), and TogetherChatPanel.tsx (the
// opt-in scrollable full history) all read from this one hook rather than
// each independently fetching/subscribing - they need to agree on exactly
// the same message list (a message the stream already faded out must
// still show up if history is opened later).
//
// Deliberately NOT appState.chatThreadMessages (the field ChatThreadScreen
// .tsx owns): this keeps its own local copy fetched from the same
// conversation, avoiding any risk of clobbering whatever the main Chat tab
// happens to have open concurrently. Messages sent here land in the exact
// same server-side conversation as regular chat (same conversationId) -
// not a session-scoped shadow thread, just a second, live view onto it.
export function useTogetherChatMessages(conversationId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Recently-arrived messages still worth showing in the fading stream -
  // a subset of `messages`, each one pruned out (by id) once its own
  // fade-and-remove timer in TogetherChatStream.tsx finishes.
  const [streamQueue, setStreamQueue] = useState<ChatMessage[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    seenIdsRef.current = new Set();
    setMessages([]);
    setStreamQueue([]);
    if (!conversationId) return;

    let cancelled = false;
    (async () => {
      const loaded = await fetchMessages(conversationId);
      if (cancelled || !loaded) return;
      loaded.messages.forEach((m) => seenIdsRef.current.add(m.id));
      setMessages(loaded.messages);
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    return onAnyNewMessage((message) => {
      if (message.conversationId !== conversationId) return;
      if (seenIdsRef.current.has(message.id)) return;
      seenIdsRef.current.add(message.id);
      setMessages((prev) => [...prev, message]);
      setStreamQueue((prev) => [...prev, message]);
    });
  }, [conversationId]);

  // The server only pushes new_message to the OTHER participant - the
  // sender has to fold their own just-sent message in locally themselves.
  function appendSentMessage(message: ChatMessage): void {
    if (seenIdsRef.current.has(message.id)) return;
    seenIdsRef.current.add(message.id);
    setMessages((prev) => [...prev, message]);
    setStreamQueue((prev) => [...prev, message]);
  }

  function removeFromStream(id: string): void {
    setStreamQueue((prev) => prev.filter((m) => m.id !== id));
  }

  return { messages, streamQueue, appendSentMessage, removeFromStream };
}
